/**
 * `/poste/arrivees` — Arrivée imprévue: a visitor with no pass at all.
 *
 * **A list, not a form.** That is the entire non-blocking mechanism: raising a
 * request returns to the list, so the next visitor is served while the first
 * one's household is still being asked. Nothing on this screen waits on a
 * résident, and nothing may be added that does.
 *
 * Recording the outcome is the gardien's act and is never inferred. The ladder
 * running out means "nobody answered", not "turned away" — an `entry_event`
 * asserts that a person physically entered or was refused, and only the person
 * at the gate knows which. So a request nobody ever acted on stays a request
 * with no event, and the reason code is what carries the gardien's judgement
 * into the syndic's log.
 */
import type { RouterOutputs } from "@gately/api";
import type {
  CardTone,
  SpotArrivalDecision,
  SpotArrivalPolicy,
  SpotArrivalReasonCode,
} from "@gately/validators";
import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Phone, Plus, TriangleAlert } from "lucide-react";

import { cn } from "@gately/ui";
import { Button } from "@gately/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { toast } from "@gately/ui/toast";
import {
  cardHeadline,
  cardTone,
  gardienActions,
  idSuffixLabel,
  ladderCountdown,
  PARCEL_DESCRIPTION_MAX,
  resolveSeverity,
  spotArrivalPurposeLabels,
  spotArrivalReasonLabels,
  validCodesFor,
} from "@gately/validators";

import { SpotArrivalCreateDialog } from "~/components/poste/spot-arrival-create-dialog";
import { StationChrome } from "~/components/station/station-chrome";
import { StationEyebrow } from "~/components/station/station-screen";
import { VisitorBadges } from "~/components/visitors/visitor-badges";
import { orpc } from "~/lib/orpc";
import { clockTime } from "~/lib/station/scan-format";
import { useOrgTimeZone } from "~/lib/use-me";
import { useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/poste/arrivees")({
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(orpc.shift.current.queryOptions()),
      context.queryClient.ensureQueryData(
        orpc.spotArrival.active.queryOptions(),
      ),
      context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
    ]);
  },
  component: SpotArrivalsPage,
});

type Request = RouterOutputs["spotArrival"]["active"][number];

/** Fast enough that the countdown and a résident's answer feel immediate. */
const POLL_MS = 3_000;

function SpotArrivalsPage() {
  const { data: me } = useSuspenseQuery(orpc.me.get.queryOptions());
  const [createOpen, setCreateOpen] = useState(false);
  // One clock for the whole list, so twenty cards do not run twenty timers.
  const [nowMs, setNowMs] = useState(() => Date.now());

  const policy = (me.activeOrg?.spotArrivalPolicy ??
    "deny") as SpotArrivalPolicy;

  const { data: requests } = useSuspenseQuery({
    ...orpc.spotArrival.active.queryOptions(),
    // Polling stops when nothing is in flight: the desk sits on this tab for
    // an hour at a time, and a resolved list has nothing left to change.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => !r.isTerminal) ? POLL_MS : false,
  });

  const live = requests.some((r) => !r.isTerminal);

  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);

  const waiting = requests.filter((r) => !r.isTerminal).length;

  return (
    <StationChrome>
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              Arrivées imprévues
            </h1>
            <StationEyebrow className="mt-1.5">
              {waiting === 0
                ? "Aucune demande en cours"
                : `${waiting} en cours`}
            </StationEyebrow>
          </div>
          <Button className="h-12" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Nouvelle arrivée
          </Button>
        </div>

        <div className="mt-6 space-y-3">
          {requests.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-8 text-center">
              <p className="text-base font-medium">Rien en attente.</p>
              <p className="text-muted-foreground mt-2 text-[0.9375rem] leading-relaxed">
                Pour un visiteur sans invitation, ouvrez une arrivée imprévue :
                les occupants sont prévenus et répondent depuis leur téléphone.
              </p>
            </div>
          ) : (
            requests.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                policy={policy}
                nowMs={nowMs}
              />
            ))
          )}
        </div>
      </div>

      <SpotArrivalCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </StationChrome>
  );
}

const TONE_CLASS: Record<CardTone, string> = {
  waiting: "border-white/12 bg-white/[0.04]",
  approved: "border-success/40 bg-success/10",
  rejected: "border-destructive/40 bg-destructive/10",
  attention: "border-warning/40 bg-warning/10",
  done: "border-white/10 bg-white/[0.02]",
};

function RequestCard({
  request,
  policy,
  nowMs,
}: {
  request: Request;
  policy: SpotArrivalPolicy;
  nowMs: number;
}) {
  const queryClient = useQueryClient();
  const terms = useTerms();
  const [reasonFor, setReasonFor] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [keepDescription, setKeepDescription] = useState("");

  const decision = request.decision;
  const tone = cardTone(request.status, decision);
  const countdown = ladderCountdown({
    createdAt: request.createdAt,
    now: nowMs,
    status: request.status,
    escalationStage: request.escalationStage,
  });
  const actions = gardienActions({
    status: request.status,
    decision,
    policy,
    purpose: request.purpose,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: orpc.spotArrival.active.key() });

  const close = useMutation(
    orpc.spotArrival.close.mutationOptions({
      onSuccess: async (_data, variables) => {
        setReasonFor(false);
        setKeepOpen(false);
        toast.success(
          variables.outcome === "admitted"
            ? "Entrée enregistrée."
            : variables.outcome === "kept_at_desk"
              ? "Colis enregistré."
              : "Refus enregistré.",
        );
        await refresh();
        await queryClient.invalidateQueries({
          queryKey: orpc.scan.today.key(),
        });
        if (variables.outcome === "kept_at_desk") {
          await queryClient.invalidateQueries({
            queryKey: orpc.parcel.listAtDesk.key(),
          });
        }
      },
      onError: (e) => toast.error(e.message || "Action impossible."),
    }),
  );

  const cancel = useMutation(
    orpc.spotArrival.cancel.mutationOptions({
      onSuccess: async () => {
        toast.success("Demande annulée.");
        await refresh();
      },
      onError: (e) => toast.error(e.message || "Action impossible."),
    }),
  );

  const busy = close.isPending || cancel.isPending;
  const timeZone = useOrgTimeZone();

  return (
    <div className={cn("rounded-xl border p-4", TONE_CLASS[tone])}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-lg font-semibold">
              {request.visitorName}
            </p>
            <VisitorBadges visitor={request.visitor} />
          </div>
          <p className="text-muted-foreground mt-0.5 text-[0.9375rem]">
            {request.destinationLabel} ·{" "}
            {spotArrivalPurposeLabels[request.purpose]}
            {idSuffixLabel(request.idSuffix)
              ? ` · pièce ${idSuffixLabel(request.idSuffix)}`
              : ""}
          </p>
        </div>
        <span className="text-muted-foreground shrink-0 font-mono text-sm">
          {clockTime(request.createdAt, timeZone)}
        </span>
      </div>

      <p className="mt-3 text-[0.9375rem] font-medium">
        {cardHeadline({
          status: request.status,
          decision,
          decidedByName: request.decidedByName,
        })}
        {countdown ? (
          <span className="text-muted-foreground font-normal">
            {" · "}
            {/* Stage 0 goes to the unit's ACTING resident — the locataire
                while it is rented — so this names neither party's title. */}
            {countdown.stage === 0
              ? "Envoyé au contact principal"
              : "Envoyé à tous les occupants"}{" "}
            · {countdown.secondsLeft}s
          </span>
        ) : null}
      </p>

      {request.status === "no_answer" ? (
        <Contacts requestId={request.id} />
      ) : null}

      {/* Actions. `pending` deliberately offers only the cancel — see
          `gardienActions`. */}
      <div className="mt-4 flex flex-wrap gap-2.5">
        {actions ? (
          <>
            <Button
              variant={actions.admit === "primary" ? "default" : "outline"}
              className="h-12"
              disabled={busy}
              onClick={() => {
                if (actions.needsReason) setReasonFor(true);
                else close.mutate({ id: request.id, outcome: "admitted" });
              }}
            >
              Laisser entrer
            </Button>
            <Button
              variant={actions.refuse === "primary" ? "destructive" : "outline"}
              className="h-12"
              disabled={busy}
              onClick={() =>
                close.mutate({ id: request.id, outcome: "turned_away" })
              }
            >
              {decision === "approved" ? "Refuser quand même" : "Refuser"}
            </Button>
            {/* Absent, not disabled, when `keep` is null: a visite has no
                parcel to leave, so the option must not exist. */}
            {actions.keep ? (
              <Button
                variant={actions.keep === "primary" ? "default" : "outline"}
                className="h-12"
                disabled={busy}
                onClick={() => setKeepOpen(true)}
              >
                {terms.keepAtDeskAction}
              </Button>
            ) : null}
          </>
        ) : null}

        {!request.isTerminal ? (
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-foreground h-12"
            disabled={busy}
            onClick={() => cancel.mutate({ id: request.id })}
          >
            Le visiteur est parti
          </Button>
        ) : null}
      </div>

      <ReasonDialog
        open={reasonFor}
        onOpenChange={setReasonFor}
        decision={decision}
        policy={policy}
        busy={close.isPending}
        onConfirm={(reason) =>
          close.mutate({ id: request.id, outcome: "admitted", reason })
        }
      />

      {/* Not a ConfirmDialog: this collects a value. ConfirmDialog is for a
          yes/no on something already decided. The description is offered here
          and not on the phone (Task 11) because the desk has a keyboard. */}
      <Dialog
        open={keepOpen}
        onOpenChange={(o) => {
          // Esc and the backdrop are ignored mid-write, like both dialogs on
          // the Colis page: the parcel is already being created, and closing
          // would hide the only spinner saying so.
          if (o || !close.isPending) setKeepOpen(o);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{terms.keepAtDeskAction}</DialogTitle>
            <DialogDescription>
              Le foyer est prévenu qu&apos;un colis l&apos;attend. Aucune entrée
              n&apos;est enregistrée : le livreur n&apos;est pas entré.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="keep-description">Description (optionnel)</Label>
            <Input
              id="keep-description"
              value={keepDescription}
              onChange={(e) => setKeepDescription(e.target.value)}
              placeholder="ex. 2 cartons"
              maxLength={PARCEL_DESCRIPTION_MAX}
            />
          </div>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              disabled={close.isPending}
              onClick={() => setKeepOpen(false)}
            >
              Annuler
            </Button>
            <Button
              disabled={close.isPending}
              onClick={() =>
                close.mutate({
                  id: request.id,
                  outcome: "kept_at_desk",
                  // Empty is omitted, never sent as "": the server's schema
                  // rejects a blank string.
                  description: keepDescription.trim() || undefined,
                })
              }
            >
              {close.isPending ? "Enregistrement…" : "Garder le colis"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * The household's numbers — the ladder's third rung, which is the gardien
 * ringing them himself. Fetched only for a `no_answer` request because the
 * server returns nothing for any other state: until the app has had its 45
 * seconds, the phone book stays shut.
 */
function Contacts({ requestId }: { requestId: string }) {
  const contacts = useQuery(
    orpc.spotArrival.contacts.queryOptions({ input: { id: requestId } }),
  );
  const rows = contacts.data ?? [];
  if (rows.length === 0) return null;

  return (
    <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
      <StationEyebrow>Appeler les occupants</StationEyebrow>
      {rows.map((c) => (
        <a
          key={c.phone}
          href={`tel:${c.phone}`}
          className="flex items-center gap-3 rounded-lg px-2.5 py-2 transition-colors hover:bg-white/8"
        >
          <Phone className="text-primary size-4 shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate text-[0.9375rem] font-medium">
            {c.name}
            {/* `isHost` marks the person the visitor is there for — shown
                ahead of `isOwner` since it is the more specific reason to
                ring this entry first. */}
            {c.isHost ? (
              <span className="text-muted-foreground font-normal">
                {" · Personne visitée"}
              </span>
            ) : null}
            {/* `isOwner` marks the ACTING resident, who is the locataire while
                the unit is rented — never labelled « propriétaire ». */}
            {c.isOwner ? (
              <span className="text-muted-foreground font-normal">
                {" · contact principal"}
              </span>
            ) : null}
          </span>
          <span className="text-muted-foreground font-mono text-sm">
            {c.phone}
          </span>
        </a>
      ))}
    </div>
  );
}

/**
 * Picking the reason an admission had no résident approval behind it.
 *
 * The exceptional codes are marked as such on the card, before the choice, not
 * afterwards in a report the gardien never sees: someone creating a flagged
 * record should know that is what they are doing.
 */
function ReasonDialog({
  open,
  onOpenChange,
  decision,
  policy,
  busy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  decision: SpotArrivalDecision | null;
  policy: SpotArrivalPolicy;
  busy: boolean;
  onConfirm: (reason: SpotArrivalReasonCode) => void;
}) {
  const terms = useTerms();
  const codes = useMemo(() => validCodesFor(decision), [decision]);
  const [choice, setChoice] = useState<SpotArrivalReasonCode | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pourquoi laisser entrer ?</DialogTitle>
          <DialogDescription>
            {decision === "rejected"
              ? `Cette arrivée a été refusée. Le motif sera signalé ${terms.toTheAdmin}.`
              : "Personne n'a répondu. Indiquez sur quelle base vous autorisez l'entrée."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {codes.map((code) => {
            const exceptional = resolveSeverity(code, policy) === "exceptional";
            const selected = choice === code;
            return (
              <button
                key={code}
                type="button"
                onClick={() => setChoice(code)}
                className={cn(
                  "flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
                  selected
                    ? "border-primary bg-primary/10"
                    : "border-border hover:bg-accent",
                )}
              >
                <span className="text-[0.9375rem] font-medium">
                  {spotArrivalReasonLabels[code]}
                </span>
                {exceptional ? (
                  <span className="text-warning flex shrink-0 items-center gap-1.5 text-xs font-medium">
                    <TriangleAlert className="size-3.5" aria-hidden="true" />
                    Signalé
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            disabled={choice === null || busy}
            onClick={() => choice && onConfirm(choice)}
          >
            {busy ? "Enregistrement…" : "Laisser entrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
